/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
import React, { useState, useRef, useEffect } from 'react';

interface VideoRecorderProps {
  onVideoRecorded: (blob: Blob) => void;
  onClose: () => void;
  maxDuration?: number; // in secondi
}

export const VideoRecorder: React.FC<VideoRecorderProps> = ({
  onVideoRecorded,
  onClose,
  maxDuration = 300 // 5 minuti default
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Inizializza stream video
  useEffect(() => {
    startStream();
    return () => {
      stopStream();
    };
  }, []);

  const startStream = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'environment' // Camera posteriore su mobile
        },
        audio: true
      });

      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('❌ Errore accesso camera/microfono:', err);
      alert('Impossibile accedere alla camera o al microfono');
      onClose();
    }
  };

  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const startRecording = () => {
    if (!stream) return;

    try {
      const options = { mimeType: 'video/webm;codecs=vp9' };
      
      // Fallback se vp9 non supportato
      const mediaRecorder = MediaRecorder.isTypeSupported(options.mimeType)
        ? new MediaRecorder(stream, options)
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        onVideoRecorded(blob);
      };

      mediaRecorder.start(100); // Raccoglie dati ogni 100ms
      setIsRecording(true);

      // Timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          
          // Stop automatico al limite
          if (newTime >= maxDuration) {
            stopRecording();
          }
          
          return newTime;
        });
      }, 1000);

      console.log('✅ Registrazione avviata');
    } catch (err) {
      console.error('❌ Errore avvio registrazione:', err);
      alert('Errore durante l\'avvio della registrazione');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      console.log('⏸️ Registrazione in pausa');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      // Riprendi timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
      
      console.log('▶️ Registrazione ripresa');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      console.log('⏹️ Registrazione terminata');
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = (): number => {
    return (recordingTime / maxDuration) * 100;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.95)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {/* Header */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        padding: '15px',
        background: 'linear-gradient(135deg, #ff6b35, #e55a2b)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10002
      }}>
        <h3 style={{ color: 'white', margin: 0, fontSize: '1.2em' }}>
          🎥 Registrazione Video
        </h3>
        <button
          onClick={() => {
            if (isRecording) {
              if (confirm('Vuoi interrompere la registrazione?')) {
                stopRecording();
                onClose();
              }
            } else {
              onClose();
            }
          }}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white',
            fontSize: '1.5em',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            cursor: 'pointer'
          }}
        >
          ×
        </button>
      </div>

      {/* Video Preview - Centrato */}
      <div style={{ 
        position: 'absolute',
        top: '80px',
        left: 0,
        right: 0,
        bottom: '200px',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        background: '#000',
        padding: '20px'
      }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: '8px'
          }}
        />
      </div>

      {/* Timer e Progress Bar - SEMPRE VISIBILE SOTTO */}
      {isRecording && (
        <div style={{ 
          position: 'absolute',
          bottom: '120px',
          left: '20px',
          right: '20px',
          background: 'rgba(26, 26, 26, 0.9)',
          padding: '15px',
          borderRadius: '8px',
          zIndex: 10001
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <span style={{ color: '#ff6b35', fontSize: '1.5em', fontWeight: 'bold' }}>
              {isPaused ? '⏸️' : '🔴'} {formatTime(recordingTime)}
            </span>
            <span style={{ color: '#999' }}>
              Max: {formatTime(maxDuration)}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div style={{
            width: '100%',
            height: '8px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${getProgressPercentage()}%`,
              height: '100%',
              background: getProgressPercentage() > 90 ? '#dc3545' : '#ff6b35',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}

      {/* Controls - FISSI IN BASSO */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '20px',
        background: '#1a1a1a',
        display: 'flex',
        gap: '10px',
        justifyContent: 'center',
        flexWrap: 'wrap',
        zIndex: 10001
      }}>
        {!isRecording ? (
          <button
            onClick={startRecording}
            style={{
              background: 'linear-gradient(135deg, #dc3545, #c82333)',
              border: 'none',
              color: 'white',
              padding: '15px 30px',
              borderRadius: '50px',
              fontSize: '1.2em',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 4px 15px rgba(220, 53, 69, 0.4)'
            }}
          >
            <span style={{ fontSize: '1.5em' }}>⏺</span>
            Inizia Registrazione
          </button>
        ) : (
          <>
            {!isPaused ? (
              <button
                onClick={pauseRecording}
                style={{
                  background: '#6c757d',
                  border: 'none',
                  color: 'white',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '1.1em',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                ⏸️ Pausa
              </button>
            ) : (
              <button
                onClick={resumeRecording}
                style={{
                  background: 'linear-gradient(135deg, #28a745, #218838)',
                  border: 'none',
                  color: 'white',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '1.1em',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(40, 167, 69, 0.4)'
                }}
              >
                ▶️ Riprendi
              </button>
            )}
            
            <button
              onClick={stopRecording}
              style={{
                background: 'linear-gradient(135deg, #dc3545, #c82333)',
                border: 'none',
                color: 'white',
                padding: '15px 30px',
                borderRadius: '8px',
                fontSize: '1.1em',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(220, 53, 69, 0.4)'
              }}
            >
              ⏹️ Termina
            </button>
          </>
        )}
      </div>
    </div>
  );
};
